import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * AppTabs — a single-line, icon-only tab bar with labels revealed on demand.
 *
 * Owner directive 2026-09-24: the tab labels must not make the navigation wrap
 * to two lines ("bad feng shui"). Tabs show ICONS only; the label appears as a
 * tooltip when the tab is pressed and held (pointer/touch) or hovered/focused.
 *
 * The bar is horizontally scrollable as a safety net but is sized so the icons
 * fit on one row at phone width.
 */
export interface AppTab {
  key: string;
  label: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  /** Short, human description shown in the portal pop-out menu (optional). */
  desc?: string;
}

interface AppTabsProps {
  tabs: AppTab[];
  active: string;
  onChange: (key: string) => void;
  /** Namespace for data-agent-id (e.g. "portal"). */
  idPrefix?: string;
}

export default function AppTabs({ tabs, active, onChange, idPrefix = 'portal' }: AppTabsProps) {
  const [tip, setTip] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAfterHold = (key: string) => {
    if (timer.current) clearTimeout(timer.current);
    // Press-and-hold (~350ms) reveals the name, per the owner's instruction.
    timer.current = setTimeout(() => setTip(key), 350);
  };
  const clear = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setTip(null);
  };

  return (
    <div className="flex flex-nowrap items-center gap-0.5 rounded-lg bg-slate-100 p-1 dark:bg-slate-800" role="tablist">
      {tabs.map(({ key, label, icon: Icon }) => {
        const isActive = key === active;
        return (
          <div key={key} className="relative min-w-0 flex-1">
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={label}
              title={label}
              data-agent-id={`${idPrefix}.tab.${key}`}
              onClick={() => onChange(key)}
              onPointerDown={() => showAfterHold(key)}
              onPointerUp={clear}
              onPointerLeave={clear}
              onPointerCancel={clear}
              onMouseEnter={() => setTip(key)}
              onMouseLeave={clear}
              onFocus={() => setTip(key)}
              onBlur={clear}
              className={`flex h-9 w-full items-center justify-center rounded-md transition-colors ${
                isActive
                  ? 'bg-white text-brand-700 shadow-sm dark:bg-slate-700 dark:text-brand-300'
                  : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              <Icon size={17} />
            </button>
            {tip === key && (
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-slate-700"
              >
                {label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** A compact, labelled section wrapper used by the portal apps. */
export function AppSection({
  title, description, actions, children,
}: { title: string; description?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">{title}</h2>
          {description && <p className="text-sm text-slate-500">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/** A simple sparkline/bar chart from numeric points. Pure SVG, no dependency. */
export function BarSeries({
  points, height = 120, label,
}: { points: { label: string; value: number }[]; height?: number; label?: string }) {
  const max = Math.max(1, ...points.map(p => p.value));
  if (!points.length) {
    return <div className="flex h-24 items-center justify-center text-xs text-slate-400">No data yet</div>;
  }
  return (
    <div>
      {label && <div className="mb-1 text-xs text-slate-500">{label}</div>}
      <div className="flex items-end gap-0.5" style={{ height }} role="img" aria-label={label || 'chart'}>
        {points.map((p) => (
          <div
            key={p.label}
            className="flex-1 rounded-t bg-brand-500/70 hover:bg-brand-500"
            style={{ height: `${Math.max(2, (p.value / max) * 100)}%` }}
            title={`${p.label}: ${p.value}`}
          />
        ))}
      </div>
    </div>
  );
}
